import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enquiriesAPI } from '../../utils/api';
import '../../styles/AdminTable.scss';

const AdminPaymentEnquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiries, categoryFilter, statusFilter, searchQuery]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await enquiriesAPI.getAll();
      const responseData = response.data || response;
      const enquiriesArray = Array.isArray(responseData.data) ? responseData.data : [];
      setEnquiries(enquiriesArray);
    } catch (err) {
      setError('Failed to load payment enquiries');
      console.error('Error fetching enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry? This action cannot be undone.')) {
      try {
        setLoading(true);
        setError('');
        await enquiriesAPI.delete(id);
        setEnquiries(prev => prev.filter(item => item.id !== id));
      } catch (err) {
        setError('Failed to delete enquiry');
        console.error('Error deleting enquiry:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const filterEnquiries = () => {
    let result = [...enquiries];

    // Category Filter
    if (categoryFilter !== 'All') {
      result = result.filter(e => e.projectCategory === categoryFilter);
    }

    // Payment Status Filter
    if (statusFilter !== 'All') {
      result = result.filter(e => e.paymentStatus === statusFilter);
    }

    // Search Query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        e =>
          (e.name && e.name.toLowerCase().includes(query)) ||
          (e.email && e.email.toLowerCase().includes(query)) ||
          (e.mobile && e.mobile.includes(query)) ||
          (e.projectTitle && e.projectTitle.toLowerCase().includes(query)) ||
          (e.enquiryId && e.enquiryId.toLowerCase().includes(query))
      );
    }

    setFilteredEnquiries(result);
  };

  const handleTogglePaymentStatus = async (id, currentStatus) => {
    try {
      const nextStatus = currentStatus === 'Paid' ? 'Pending' : 'Paid';
      if (!window.confirm(`Are you sure you want to mark this enquiry as ${nextStatus}?`)) {
        return;
      }
      setLoading(true);
      await enquiriesAPI.updatePaymentStatus(id, nextStatus);
      await fetchEnquiries();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to resend the payment instruction email?')) {
        return;
      }
      setLoading(true);
      await enquiriesAPI.resendPaymentEmail(id);
      alert('Payment email resent successfully!');
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Failed to resend payment email');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e, id) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingId(id);
      const formData = new FormData();
      formData.append('file', file);
      await enquiriesAPI.uploadPaymentProof(id, formData);
      alert('Payment proof uploaded successfully!');
      await fetchEnquiries();
    } catch (err) {
      console.error('Error uploading proof:', err);
      alert('Failed to upload proof');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownloadProof = async (id, enquiryId) => {
    try {
      const response = await enquiriesAPI.downloadPaymentProof(id);
      // Since responseType is blob, response.data contains the blob.
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data || response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proof_${enquiryId || id}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading proof:', err);
      alert('Failed to download payment proof. Ensure a file was uploaded.');
    }
  };

  // Stats
  const stats = {
    total: enquiries.length,
    paid: enquiries.filter(e => e.paymentStatus === 'Paid').length,
    pending: enquiries.filter(e => e.paymentStatus === 'Pending').length,
    revenue: enquiries
      .filter(e => e.paymentStatus === 'Paid')
      .reduce((sum, e) => sum + (parseFloat(e.projectPrice) || 0), 0),
  };

  return (
    <div className="admin-page admin-table-styles">
      <div className="page-header">
        <h1>Payment & Accounts CRM</h1>
        <p>Manage project payments, track customer screenshots, and verify transactions</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="stat-card blue">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Enquiries</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">🟡</div>
          <div className="stat-content">
            <h3>Pending Payments</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">🟢</div>
          <div className="stat-content">
            <h3>Paid / Verified</h3>
            <p className="stat-value">{stats.paid}</p>
          </div>
        </div>
        <div className="stat-card green" style={{ borderLeftColor: '#00526a' }}>
          <div className="stat-icon" style={{ backgroundColor: '#e2f9ff', color: '#00526a' }}>💰</div>
          <div className="stat-content">
            <h3>Total Revenue</h3>
            <p className="stat-value">₹{stats.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="admin-actions-bar" style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="filters-group" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
          >
            <option value="All">All Categories</option>
            <option value="Software">Software</option>
            <option value="MBA">MBA</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
          >
            <option value="All">All Payment Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
          </select>

          <input
            type="text"
            placeholder="Search ID, name, email, mobile, project..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '300px', outline: 'none' }}
          />
        </div>

        <div className="buttons-group" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn-primary"
            onClick={fetchEnquiries}
            disabled={loading}
            style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Main Content Table */}
      <div className="content-wrapper">
        <div className="table-container" style={{ width: '100%' }}>
          {loading && enquiries.length === 0 ? (
            <p style={{ padding: '20px' }}>Loading payment records...</p>
          ) : filteredEnquiries.length === 0 ? (
            <p style={{ padding: '20px' }}>No matching payment enquiries found</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Enquiry ID</th>
                  <th>Customer</th>
                  <th>Project Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Proof</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td>{new Date(enquiry.createdAt || enquiry.created_at).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 600, color: '#4a5568' }}>{enquiry.enquiryId || `PI-${enquiry.id}`}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{enquiry.name}</div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>{enquiry.email}</div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>{enquiry.mobile}</div>
                    </td>
                    <td style={{ maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={enquiry.projectTitle}>
                      {enquiry.projectTitle}
                    </td>
                    <td>
                      <span className={`badge ${enquiry.projectCategory === 'Software' ? 'blue' : 'orange'}`} style={{ backgroundColor: enquiry.projectCategory === 'Software' ? '#ebf8ff' : '#feebc8', color: enquiry.projectCategory === 'Software' ? '#2b6cb0' : '#c05621', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                        {enquiry.projectCategory || 'MBA'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>₹{(enquiry.projectPrice || 0).toLocaleString('en-IN')}</td>
                    <td>
                      <button
                        onClick={() => handleTogglePaymentStatus(enquiry.id, enquiry.paymentStatus)}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0
                        }}
                        title="Click to toggle payment status"
                      >
                        <span
                          className="badge"
                          style={{
                            backgroundColor: enquiry.paymentStatus === 'Paid' ? '#01b574' : '#FFA500',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            display: 'inline-block'
                          }}
                        >
                          {enquiry.paymentStatus || 'Pending'}
                        </span>
                      </button>
                    </td>
                    <td>
                      {enquiry.paymentProof ? (
                        <button
                          className="btn-view"
                          onClick={() => handleDownloadProof(enquiry.id, enquiry.enquiryId)}
                          style={{ padding: '4px 8px', background: '#3182ce', color: 'white', borderRadius: '4px', border: 'none', fontSize: '12px', cursor: 'pointer' }}
                        >
                          📥 Download
                        </button>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label
                            htmlFor={`proof-file-${enquiry.id}`}
                            style={{
                              padding: '4px 8px',
                              background: '#edf2f7',
                              color: '#4a5568',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              textAlign: 'center',
                              border: '1px solid #cbd5e0',
                              fontWeight: 500
                            }}
                          >
                            {uploadingId === enquiry.id ? 'Uploading...' : '📁 Upload'}
                          </label>
                          <input
                            type="file"
                            id={`proof-file-${enquiry.id}`}
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileChange(e, enquiry.id)}
                            disabled={uploadingId !== null}
                          />
                        </div>
                      )}
                    </td>
                    <td className="actions" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        className="btn-view"
                        onClick={() => {
                          const routePrefix = enquiry.projectCategory === 'Software' ? 'software' : 'mba';
                          navigate(`/admin/${routePrefix}-enquiries/${enquiry.id}`);
                        }}
                        style={{ padding: '6px 10px', fontSize: '12px' }}
                      >
                        Details
                      </button>
                      <button
                        className="btn-primary"
                        onClick={() => handleResendEmail(enquiry.id)}
                        style={{ padding: '6px 10px', background: '#4a5568', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                        title="Resend payment instruction email"
                      >
                        ✉ Resend
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteEnquiry(enquiry.id)}
                        title="Delete Enquiry"
                        style={{
                          background: '#fff5f5',
                          color: '#e53e3e',
                          border: '1px solid #fed7d7',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(229, 62, 62, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e53e3e';
                          e.target.style.color = 'white';
                          e.target.style.borderColor = '#e53e3e';
                          e.target.style.boxShadow = '0 4px 10px rgba(229, 62, 62, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#fff5f5';
                          e.target.style.color = '#e53e3e';
                          e.target.style.borderColor = '#fed7d7';
                          e.target.style.boxShadow = '0 2px 4px rgba(229, 62, 62, 0.05)';
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentEnquiries;
